<template>
  <div>
    <Navbar />

    <main class="auth-page">
      <div class="auth-bg">
        <div class="auth-bg-overlay"></div>
      </div>

      <div class="auth-container">

        <!-- Left: Brand -->
        <div class="auth-brand">
          <div class="brand-content">
            <div class="brand-badge">
              <font-awesome-icon icon="th-large" class="me-1" /> All-in-One Business Platform
            </div>
            <h2 class="brand-title">
              Every tool your business needs. One platform. Zero compromises.
            </h2>
            <p class="brand-sub">
              Mapiibs unifies inventory, accounting, payroll, HR, sales, invoicing and more — giving your team one single source of truth.
            </p>
            <ul class="brand-perks">
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Real-time inventory across multiple locations</li>
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Automated payroll &amp; HR management</li>
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Full double-entry accounting &amp; financial reports</li>
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Invoicing, billing, debtors &amp; creditors</li>
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> 24/7 secure cloud access from anywhere</li>
            </ul>
            <div class="brand-modules">
              <span class="brand-mod"><font-awesome-icon icon="boxes" class="bm-icon" /> Inventory</span>
              <span class="brand-mod"><font-awesome-icon icon="calculator" class="bm-icon" /> Accounting</span>
              <span class="brand-mod"><font-awesome-icon icon="coins" class="bm-icon" /> Payroll</span>
              <span class="brand-mod"><font-awesome-icon icon="users" class="bm-icon" /> HR</span>
              <span class="brand-mod"><font-awesome-icon icon="file-invoice" class="bm-icon" /> Invoicing</span>
              <span class="brand-mod"><font-awesome-icon icon="shopping-cart" class="bm-icon" /> Sales</span>
              <span class="brand-mod"><font-awesome-icon icon="truck" class="bm-icon" /> Purchases</span>
              <span class="brand-mod"><font-awesome-icon icon="chart-bar" class="bm-icon" /> Reports</span>
            </div>
          </div>
        </div>

        <!-- Right: Form card -->
        <div class="auth-card-wrap">
          <div class="auth-card">

            <div class="auth-card-header">
              <h3 class="auth-title">Welcome back</h3>
              <p class="auth-sub">Sign in to your account to continue.</p>
            </div>

            <!-- Validation errors -->
            <div v-if="hasErrors" class="error-box">
              <div class="error-box-title">
                <font-awesome-icon icon="exclamation-circle" class="me-1" /> Login failed:
              </div>
              <ul class="error-list">
                <li v-for="(err, i) in flatErrors" :key="i">{{ err }}</li>
              </ul>
            </div>

            <form @submit.prevent="loginUser" novalidate>

              <div class="form-group-styled">
                <label class="form-label-styled">Email Address</label>
                <input
                  v-model="email"
                  type="email"
                  class="form-input-styled"
                  placeholder="you@company.com"
                  autocomplete="email"
                  required
                />
              </div>

              <div class="form-group-styled">
                <div class="label-row">
                  <label class="form-label-styled">Password</label>
                  <router-link to="/auth/reset-password" class="forgot-link">Forgot password?</router-link>
                </div>
                <div class="pw-wrap">
                  <input
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-input-styled"
                    placeholder="Enter your password"
                    autocomplete="current-password"
                    required
                  />
                  <button type="button" class="pw-toggle" @click="showPassword = !showPassword" tabindex="-1">
                    <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                class="submit-btn"
                :disabled="loading || !email || !password"
              >
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Signing in...' : 'Sign In' }}
              </button>

            </form>

            <div class="auth-card-footer">
              <p>Don't have an account? <router-link to="/auth/register" class="auth-link">Create one free</router-link></p>
            </div>

          </div>
        </div>

      </div>
    </main>

    <Footer />
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import Navbar from '../components/Navbar';
import Footer from '../components/footer';

export default {
  name: 'LoginPage',

  components: { Navbar, Footer },

  setup() {
    const router = useRouter();
    const toast  = useToast();
    const store  = useStore();

    const email    = ref('');
    const password = ref('');
    const loading  = ref(false);
    const showPassword      = ref(false);
    const validationErrors  = ref({});

    const hasErrors  = computed(() => Object.keys(validationErrors.value).length > 0);
    const flatErrors = computed(() => Object.values(validationErrors.value).flat());

    const normaliseErrors = (msg) => {
      if (!msg) return {};
      if (typeof msg === 'string')  return { general: [msg] };
      if (Array.isArray(msg))       return { general: msg.flat() };
      return msg; // already an object
    };

    const loginUser = async () => {
      if (!email.value || !password.value) return;
      loading.value = true;
      validationErrors.value = {};

      try {
        const { data } = await axios.post('/login-web', {
          email:    email.value,
          password: password.value,
        });

        // Super user whose setup_completed === false returns status 'setup_required'
        if (data.status === 'setup_required') {
          const setupToken = data.data?.setup_token;
          if (setupToken) localStorage.setItem('setup_token', setupToken);
          toast.warning(data.message ?? 'Please complete your account setup.');
          router.push('/auth/account-setup');
          return;
        }

        const { accessToken, company_code } = data.data.original;
        localStorage.setItem('token', accessToken);
        if (company_code) {
          localStorage.setItem('companyCode', company_code);
          store.dispatch('setCompanyCode', company_code);
        }
        axios.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
        toast.success('Login successful.');
        router.push(company_code ? `/${company_code}` : '/');
      } catch (err) {
        const status  = err.response?.status;
        const message = err.response?.data?.message;
        if (status === 402) {
          toast.error(message ?? 'Please verify your email to continue.');
          router.push('send-email-verifcation');
        } else {
          validationErrors.value = normaliseErrors(message);
          toast.error(message ?? 'An error occurred. Please try again.');
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      email, password, loading, showPassword,
      validationErrors, hasErrors, flatErrors,
      loginUser,
    };
  },
};
</script>

<style scoped>
/* ── Page shell ──────────────────────────────────────────────────────── */
.auth-page {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: stretch;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: url('/static/img/banner-bg-5.jpg') center/cover no-repeat;
}
.auth-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(5,27,53,.96) 0%, rgba(0,74,173,.82) 100%);
}

.auth-container {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  padding: 40px 24px;
  gap: 48px;
  align-items: center;
  min-height: 100vh;
}

/* ── Brand panel ─────────────────────────────────────────────────────── */
.auth-brand   { display:flex; align-items:center; }
.brand-content { display:flex; flex-direction:column; gap:20px; }

.brand-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,.1);
  border: 1px solid rgba(255,255,255,.18);
  color: rgba(255,255,255,.9);
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .07em;
  width: fit-content;
}

.brand-title { font-size:clamp(22px,3vw,32px); font-weight:700; color:#fff; line-height:1.35; margin:0; }
.brand-sub   { font-size:15px; color:rgba(255,255,255,.65); margin:0; }
.brand-perks { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:11px; }
.brand-perks li { display:flex; align-items:center; gap:10px; font-size:14px; font-weight:500; color:rgba(255,255,255,.82); }
.perk-icon   { color:#4cde80; font-size:15px; flex-shrink:0; }

.brand-modules {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.brand-mod {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.14);
  color: rgba(255,255,255,.72);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: .02em;
}
.bm-icon { font-size:10px; color:rgba(255,255,255,.5); }

/* ── Form card ───────────────────────────────────────────────────────── */
.auth-card-wrap { display:flex; align-items:center; }
.auth-card {
  background: #fff;
  border-radius: 16px;
  padding: 40px 36px;
  width: 100%;
  box-shadow: 0 24px 60px rgba(0,0,0,.22);
}

.auth-card-header { margin-bottom:26px; }
.auth-title { font-size:24px; font-weight:800; color:#1a2b45; margin:0 0 6px; }
.auth-sub   { font-size:14px; color:#7a90b0; margin:0; }
.auth-link  { color:#004AAD; font-weight:700; text-decoration:none; }
.auth-link:hover { text-decoration:underline; }

/* ── Error box ───────────────────────────────────────────────────────── */
.error-box       { background:#fff5f5; border-left:3px solid #e03535; border-radius:0 8px 8px 0; padding:12px 14px; margin-bottom:20px; }
.error-box-title { font-size:13px; font-weight:700; color:#e03535; margin-bottom:6px; display:flex; align-items:center; }
.error-list      { margin:0; padding-left:16px; }
.error-list li   { font-size:12.5px; color:#c02828; margin-bottom:3px; }
.me-1 { margin-right:4px; }

/* ── Form elements ───────────────────────────────────────────────────── */
.form-group-styled { display:flex; flex-direction:column; gap:6px; margin-bottom:16px; }
.form-label-styled { font-size:13px; font-weight:600; color:#1a2b45; }

.label-row   { display:flex; align-items:center; justify-content:space-between; }
.forgot-link { font-size:12px; font-weight:600; color:#004AAD; text-decoration:none; }
.forgot-link:hover { text-decoration:underline; }

.form-input-styled {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #dde5f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1a2b45;
  background: #fff;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  font-family: inherit;
}
.form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.09); }

/* ── Password wrap ───────────────────────────────────────────────────── */
.pw-wrap { position:relative; display:flex; align-items:center; }
.pw-wrap .form-input-styled { padding-right:42px; }
.pw-toggle { position:absolute; right:12px; border:none; background:transparent; color:#7a90b0; cursor:pointer; font-size:14px; padding:0; transition:color .15s; }
.pw-toggle:hover { color:#1a2b45; }

/* ── Submit ──────────────────────────────────────────────────────────── */
.submit-btn {
  width: 100%;
  padding: 13px;
  background: #004AAD;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, transform .1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
}
.submit-btn:hover:not(:disabled)  { background:#003b8e; }
.submit-btn:active:not(:disabled) { transform:scale(.98); }
.submit-btn:disabled { opacity:.5; cursor:not-allowed; }

/* ── Card footer ─────────────────────────────────────────────────────── */
.auth-card-footer { margin-top:22px; padding-top:16px; border-top:1px solid #f0f4f8; text-align:center; font-size:13px; color:#7a90b0; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width: 860px) {
  .auth-container { grid-template-columns:1fr; max-width:480px; }
  .auth-brand     { display:none; }
  .auth-card-wrap { padding:0; }
}

@media (max-width: 480px) {
  .auth-card      { padding:28px 20px; }
  .auth-container { padding:24px 16px; }
}
</style>